import type {
  DataDiscoveryMetricsRequestDTO,
  DataDiscoveryUniverseMetricsResponseDTO,
  DataDiscoveryMetricsVolScaledResponseDTO,
  DataDiscoveryResponseDTO,
  DataDiscoveryVolScaledPortfolioMetricsRequestDTO,
  DataDiscoveryVolScaledStrategyMetricsRequestDTO,
  PortfolioItemResponseDTO,
  PortfolioSummaryResponseDTO,
  StrategyItemResponseDTO,
  StrategyCategoryResponseDTO,
  DataDiscoveryPortfolioMetricsResponseDTO,
  DSBStrategyItemResponseDTO,
  DSBPortfolioItemResponseDTO,
} from '@/api-v2/web/discover';
import {
  getUniverse,
  getNonVolScaledUniverseMetrics,
  getQuantSpacePositions,
  getVolScaledPortfolioMetrics,
  getPortfolioSummary,
  getVolScaledUniverseMetrics,
  getFxRates,
  getSignals,
  getCash,
  getEquities,
  getEquitiesMetrics,
  getStructuredProducts,
  getStructuredProductMetrics,
  getEvestmentFunds,
  getEvestmentFundsMetrics,
  getNonVolScaledPortfoliosMetrics,
  getPortfolios,
  getMorningstarFunds,
  getMorningstarFundsMetrics,
  getPrivateTracks,
  getPrivateTracksMetrics,
  getRetiredStrategies,
  getStrategyCategories,
} from '@/api-v2/web/discover';
import { getAllFavorites } from '@/api-v2/web/favorites/endpoints';
import { TimeSeriesEntityTypeConstants } from '@/constants/TimeSeriesEntityTypeConstants';
import { DataType, normalizePortfolioType, normalizeType } from '@/types/setting';
import type { VQQueryOptions } from '@/types/VueQueryTypes';
import type { QueryObserverResult, UseQueryOptions } from '@tanstack/vue-query';
import { useQueries, useQuery, queryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import { computed, ref, unref } from 'vue';
import type {
  DataDiscoveryUniverseQuantSpaceRequestDTO,
  DataDiscoveryUniverseQuantSpaceResponseDTO,
} from '@/api-v2/web/discover/types/DataDiscoveryQuantSpaceDTO';
import type { IMetric } from '@/types/strategy';
import { useConstituentAnalysisDate } from '../useConstituentRiskUtilities';
import { chainEnabled, unwrap } from '@/utils/queries';
import useLoadingWhenEnabledQuery from './useLoadingWhenEnabledQuery';
import { useRoute } from 'vue-router';
import useRouteChecks from '../useRouteChecks';
import { useIsOnPositionView } from '../useCompositionHistoryMode';
import { notNull } from '@/utils/notnull';
import type { MaybeRef } from '@vueuse/shared';
import type { DiscoverPeriod } from '@/constants/Discover';
import type { DataDiscoveryUniverse } from '@/api-v2/web/discover/types/DataDiscoveryUniverse';
import { flatten } from 'lodash';
import { markRawRecursive } from './markRawRecursive';
import { liveObjectKeys, liveObjectsSelect } from './useLiveObjects';
import { favoriteKeys, favoritesSelect } from './useFavorites';
import { withLimitedQueryFns } from './useLimitedQueries';
import { getLiveObjects } from '@/api-v2/web/strategies';
import {
  getCompositeTicker,
  getPortfolioNameBasedOnPreference,
  getStrategyDisplayNameBasedOnPreference,
  sortItemsByDisplayName,
} from '@/utils/strategy';
import { useUserQueryOptions } from './useUserQueries';
import type { IUserInfoResponse } from '@/api-v2/web/user';
import getEnv from '../../utils/getEnv';
import { PortfolioType } from '@/types/portfolio';
import type { IndexOptionType } from '@/api-v2/web/discover/types/IndexOptionType';

const keys = {
  /**
   * Note: keep this in sync with `discoveryAllKey()` in {@link file://./useStrategies.ts} and
   * {@link file://./usePortfolioTreeData.ts}.
   */
  all: () => [{ scope: 'discover' }] as const,

  /**
   * As of September 2025, this contains everything but:
   * - signals
   * - cash
   * - stocks
   * - structured products
   * - portfolios
   * - eVestment funds
   * - morningstar funds
   * - private tracks
   */
  limitedUniverse: () => [{ ...keys.all()[0], entity: 'limited-universe' }] as const,
  portfolios: () => [{ ...keys.all()[0], entity: 'portfolios' }] as const,
  portfolioSummary: () => [{ ...keys.all()[0], entity: 'portfolio-summary' }],
  nonVolScaledUniverseMetrics: (query: Ref<DataDiscoveryMetricsRequestDTO>) => [
    { ...keys.all()[0], entity: 'non-vol-scaled-universe-metrics', query },
  ],
  nonVolScaledPortfoliosMetrics: (query: Ref<DataDiscoveryMetricsRequestDTO>) => [
    { ...keys.all()[0], entity: 'non-vol-scaled-portfolios-metrics', query },
  ],
  quantSpacePositions: (query: Ref<DataDiscoveryUniverseQuantSpaceRequestDTO>) =>
    [{ ...keys.all()[0], entity: 'quant-space-positions', query }] as const,
  volScaledUniverseMetrics: (query: Ref<DataDiscoveryVolScaledStrategyMetricsRequestDTO>) => [
    { ...keys.all()[0], entity: 'vol-scaled-universe-metrics', query },
  ],
  volScaledPortfolioMetrics: (query: Ref<DataDiscoveryVolScaledPortfolioMetricsRequestDTO>) => [
    { ...keys.all()[0], entity: 'vol-scaled-portfolio-metrics', query },
  ],
  fxRates: (date: Ref<string | null>) => [{ ...keys.all()[0], entity: 'fxRates', date }] as const,
  equities: () => [{ ...keys.all()[0], entity: 'equities' }] as const,
  equitiesMetrics: (query: Ref<DataDiscoveryMetricsRequestDTO>) => [
    { ...keys.all()[0], entity: 'equities-metrics', query },
  ],
  structuredProducts: () => [{ ...keys.all()[0], entity: 'structured-products' }] as const,
  structuredProductsMetrics: (query: Ref<DataDiscoveryMetricsRequestDTO>) => [
    { ...keys.all()[0], entity: 'structured-products-metrics', query },
  ],
  evestmentFunds: () => [{ ...keys.all()[0], entity: 'evestment-funds' }] as const,
  evestmentFundsMetrics: (query: Ref<DataDiscoveryMetricsRequestDTO>) => [
    { ...keys.all()[0], entity: 'evestment-funds-metrics', query },
  ],
  morningstarFunds: () => [{ ...keys.all()[0], entity: 'morningstar-funds' }] as const,
  morningstarFundsMetrics: (query: Ref<DataDiscoveryMetricsRequestDTO>) => [
    { ...keys.all()[0], entity: 'morningstar-funds-metrics', query },
  ],
  privateTracks: () => [{ ...keys.all()[0], entity: 'private-tracks' }] as const,
  privateTracksMetrics: (query: Ref<DataDiscoveryMetricsRequestDTO>) => [
    { ...keys.all()[0], entity: 'private-tracks-metrics', query },
  ],
  retiredStrategies: () => [{ ...keys.all()[0], entity: 'retired-strategies' }] as const,
  signals: () => [{ ...keys.all()[0], entity: 'signals' }] as const,
  cash: () => [{ ...keys.all()[0], entity: 'cash' }] as const,
  strategyCategories: () => [{ ...keys.all()[0], entity: 'strategy-categories' }] as const,
};

/**
 * IMPORTANT: you should be adding `markRawRecursive` to the result of the select function if you are not using
 * a `combine` function which does this for you.
 */
const select = {
  /**
   * Does NOT return the result with `markRawRecursive`
   */
  allStrategies: (universe: DataDiscoveryResponseDTO): StrategyItemResponseDTO[] => universe.strategy ?? [],
  /**
   * Returns only data matching the following types:
   * - TimeSeriesEntityTypeConstants.STRATEGY
   * - TimeSeriesEntityTypeConstants.BENCHMARK
   * - TimeSeriesEntityTypeConstants.PRIVATE_STRATEGY
   * - TimeSeriesEntityTypeConstants.THEMATIC
   * - TimeSeriesEntityTypeConstants.PUBLIC_THEMATIC
   *
   * Does NOT return portfolios
   *
   * Does NOT return the result with `markRawRecursive`
   */
  universeForBofA: (universe: DataDiscoveryResponseDTO): DataDiscoveryResponseDTO => ({
    strategy: (universe.strategy ?? []).filter((s) =>
      [
        TimeSeriesEntityTypeConstants.STRATEGY,
        TimeSeriesEntityTypeConstants.BENCHMARK,
        TimeSeriesEntityTypeConstants.PRIVATE_STRATEGY,
        TimeSeriesEntityTypeConstants.THEMATIC,
        TimeSeriesEntityTypeConstants.PUBLIC_THEMATIC,
      ].includes(s.type),
    ),
    colors: universe.colors,
  }),

  /**
   * Returns all portfolios, excluding live portfolios.
   * For more details on this, see the comment on `shouldTreatRootItemAsPortfolio`
   *
   * Returns the result with `markRawRecursive` to ensure that the data is not reactive
   */
  allPortfolios: (portfolios: PortfolioItemResponseDTO[]): DSBPortfolioItemResponseDTO[] =>
    markRawRecursive(
      sortItemsByDisplayName(
        (portfolios ?? [])
          .filter((o) => !o.shouldExposePositions)
          .map((p) => ({
            ...p,
            virtualKey: p.portfolioId,
            displayName: p.name,
          })),
      ),
    ),

  /**
   * Returns portfolios by id, excluding live portfolios
   * For more details on this, see the comment on `shouldTreatRootItemAsPortfolio`
   *
   * Returns the result with `markRawRecursive` to ensure that the data is not reactive
   */
  allPortfoliosById: (portfolios: PortfolioItemResponseDTO[]): Map<string, DSBPortfolioItemResponseDTO> =>
    markRawRecursive(
      new Map(
        (portfolios ?? [])
          .filter((o) => !o.shouldExposePositions)
          .map((portfolio) => {
            const obj: DSBPortfolioItemResponseDTO = {
              ...portfolio,
              virtualKey: portfolio.portfolioId,
              displayName: portfolio.name,
            };

            return [portfolio.portfolioId, obj];
          }),
      ),
    ),
  /**
   * Returns portfolios by slug, excluding live portfolios
   * For more details on this, see the comment on `shouldTreatRootItemAsPortfolio`
   *
   * Returns the result with `markRawRecursive` to ensure that the data is not reactive
   */
  allPortfoliosBySlug: (portfolios: PortfolioItemResponseDTO[]): Map<string, DSBPortfolioItemResponseDTO> =>
    markRawRecursive(
      new Map(
        (portfolios ?? [])
          .filter((o) => !o.shouldExposePositions)
          .map((portfolio) => [
            portfolio.slug,
            {
              ...portfolio,
              virtualKey: portfolio.portfolioId,
              displayName: portfolio.name,
            },
          ]),
      ),
    ),

  /**
   * Returns all portfolio names, excluding live portfolios
   * For more details on this, see the comment on `shouldTreatRootItemAsPortfolio`
   *
   * Returns the result with `markRawRecursive` to ensure that the data is not reactive
   */
  allPortfolioNames: (portfolios: PortfolioItemResponseDTO[]): string[] =>
    markRawRecursive((portfolios ?? []).filter((o) => !o.shouldExposePositions).map((portfolio) => portfolio.name)),

  /**
   * Does NOT return the result with `markRawRecursive`
   */
  addCompositeTicker: (strategies: StrategyItemResponseDTO[]): StrategyItemResponseDTO[] =>
    strategies.map((strategy) => {
      const tickerToUse = getCompositeTicker(strategy.ticker);
      if (!tickerToUse) return strategy;

      return {
        ...strategy,
        ticker: tickerToUse,
      };
    }),

  /**
   * Does NOT return the result with `markRawRecursive`
   */
  stripCurncySuffixFromTicker: (strategies: StrategyItemResponseDTO[]): StrategyItemResponseDTO[] =>
    strategies.map((strategy) => {
      return {
        ...strategy,
        ticker: strategy.ticker?.map((t) => t.replace(/ Curncy$/, '')),
      };
    }),

  /**
   * Selects strategies from the universe matching DataType.STRATEGY
   *
   * Does NOT return the result with `markRawRecursive`
   */
  strategiesFromUniverse: (universe: DataDiscoveryResponseDTO): StrategyItemResponseDTO[] =>
    (universe.strategy ?? []).filter((s) => normalizeType(s) === DataType.STRATEGY),

  /**
   * Selects strategies from the universe matching DataType.STRATEGY and DataType.PRIVATE_STRATEGY
   *
   * Does NOT return the result with `markRawRecursive`
   */
  strategiesFromUniverseBofA: (universe: DataDiscoveryResponseDTO): StrategyItemResponseDTO[] =>
    (universe.strategy ?? []).filter((s) => {
      const type = normalizeType(s);
      return type === DataType.STRATEGY || type === DataType.PRIVATE_STRATEGY;
    }),

  /**
   * Selects strategies from strategy items matching DataType.STRATEGY
   *
   * Does NOT return the result with `markRawRecursive`
   */
  strategies: (strategyItems: StrategyItemResponseDTO[]): StrategyItemResponseDTO[] =>
    strategyItems.filter((s) => normalizeType(s) === DataType.STRATEGY),

  /**
   * Does NOT return the result with `markRawRecursive`
   */
  privateTracks: (strategyItems: StrategyItemResponseDTO[]): StrategyItemResponseDTO[] =>
    strategyItems.filter((s) => normalizeType(s) === DataType.PRIVATE_TRACK),

  /**
   * Selects funds from the universe matching DataType.FUND
   *
   * Does NOT return the result with `markRawRecursive`
   */
  fundsFromUniverse: (universe: DataDiscoveryResponseDTO): StrategyItemResponseDTO[] =>
    (universe.strategy ?? []).filter((s) => normalizeType(s) === DataType.FUND),

  /**
   * Selects funds from the strategy items matching DataType.FUND
   *
   * Does NOT return the result with `markRawRecursive`
   */
  funds: (strategyItems: StrategyItemResponseDTO[]): StrategyItemResponseDTO[] =>
    strategyItems.filter((s) => normalizeType(s) === DataType.FUND),

  /**
   * Selects thematics from the universe matching DataType.THEMATIC
   *
   * Does NOT return the result with `markRawRecursive`
   */
  thematicsFromUniverse: (universe: DataDiscoveryResponseDTO): StrategyItemResponseDTO[] =>
    (universe.strategy ?? []).filter((s) => normalizeType(s) === DataType.THEMATIC),

  /**
   * Selects thematics from the universe matching DataType.THEMATIC and DataType.PRIVATE_THEMATIC
   *
   * Does NOT return the result with `markRawRecursive`
   */
  thematicsFromUniverseBofA: (universe: DataDiscoveryResponseDTO): StrategyItemResponseDTO[] =>
    (universe.strategy ?? []).filter((s) => {
      const type = normalizeType(s);
      return type === DataType.THEMATIC || type === DataType.PRIVATE_THEMATIC;
    }),

  /**
   * Selects thematics from strategy items matching DataType.THEMATIC
   *
   * Does NOT return the result with `markRawRecursive`
   */
  thematics: (strategyItems: StrategyItemResponseDTO[]): StrategyItemResponseDTO[] =>
    strategyItems.filter((s) => normalizeType(s) === DataType.THEMATIC),

  /**
   * Selects thematics from strategy items matching DataType.THEMATIC and DataType.PRIVATE_THEMATIC
   *
   * Does NOT return the result with `markRawRecursive`
   */
  thematicsBofA: (strategyItems: StrategyItemResponseDTO[]): StrategyItemResponseDTO[] =>
    strategyItems.filter((s) => {
      const type = normalizeType(s);
      return type === DataType.THEMATIC || type === DataType.PRIVATE_THEMATIC;
    }),
  /**
   * Selects benchmarks from the universe matching DataType.BENCHMARK
   *
   * Does NOT return the result with `markRawRecursive`
   */
  strategyBenchmarksFromUniverse: (universe: DataDiscoveryResponseDTO): StrategyItemResponseDTO[] =>
    (universe.strategy ?? []).filter((s) => normalizeType(s) === DataType.BENCHMARK),

  /**
   * Selects benchmarks from the strategy items matching DataType.BENCHMARK
   *
   * Does NOT return the result with `markRawRecursive`
   */
  benchmarks: (strategies: StrategyItemResponseDTO[]): StrategyItemResponseDTO[] =>
    strategies.filter((s) => normalizeType(s) === DataType.BENCHMARK),

  /**
   * Selects portfolio benchmarks from the portfolios matching DataType.BENCHMARK
   *
   * Does NOT return the result with `markRawRecursive`
   */
  portfolioBenchmarks: (portfolios: PortfolioItemResponseDTO[]): PortfolioItemResponseDTO[] =>
    (portfolios ?? []).filter((o) => normalizePortfolioType(o) === DataType.BENCHMARK && !o.shouldExposePositions),

  /**
   * Selects pure factors from the universe matching DataType.PURE_FACTOR
   *
   * Does NOT return the result with `markRawRecursive`
   */
  pureFactorsFromUniverse: (universe: DataDiscoveryResponseDTO): StrategyItemResponseDTO[] =>
    (universe.strategy ?? []).filter((s) => normalizeType(s) === DataType.PURE_FACTOR),

  /**
   * Selects market data from the universe matching DataType.MARKET_DATA
   *
   * Does NOT return the result with `markRawRecursive`
   */
  marketDataFromUniverse: (universe: DataDiscoveryResponseDTO): StrategyItemResponseDTO[] =>
    (universe.strategy ?? []).filter((s) => normalizeType(s) === DataType.MARKET_DATA),

  /**
   * Selects QIS Baskets from portfolios matching DataType.QIS_BASKET
   *
   * Does NOT return the result with `markRawRecursive`
   */
  qisBaskets: (portfolios: PortfolioItemResponseDTO[]): PortfolioItemResponseDTO[] =>
    (portfolios ?? []).filter((o) => normalizePortfolioType(o) === DataType.QIS_BASKET && !o.shouldExposePositions),

  /**
   * Selects Equity Baskets from portfolios matching DataType.EQUITY_BASKET
   *
   * Does NOT return the result with `markRawRecursive`
   */
  equityBaskets: (portfolios: PortfolioItemResponseDTO[]): PortfolioItemResponseDTO[] =>
    (portfolios ?? []).filter((o) => normalizePortfolioType(o) === DataType.EQUITY_BASKET && !o.shouldExposePositions),
  /**
   * Selects all portfolios that are neither QIS Baskets nor Equity Baskets
   *
   * Does NOT return the result with `markRawRecursive`
   */
  nonBasketPortfolios: (portfolios: PortfolioItemResponseDTO[]): PortfolioItemResponseDTO[] =>
    (portfolios ?? []).filter(
      (o) =>
        normalizePortfolioType(o) !== DataType.QIS_BASKET &&
        normalizePortfolioType(o) !== DataType.EQUITY_BASKET &&
        !o.shouldExposePositions,
    ),
};

const getFavoritesQueryOptions = (options: VQQueryOptions<Map<string, string>> = {}) =>
  queryOptions({
    queryKey: favoriteKeys.list(),
    queryFn: ({ signal }) => getAllFavorites(signal),
    ...options,
    select: favoritesSelect.mapIdToFavoriteId,
  });

type UserQueryResult = QueryObserverResult<IUserInfoResponse, Error>;
type StrategyQueryResult = QueryObserverResult<StrategyItemResponseDTO[], Error>;
type PortfolioQueryResult = QueryObserverResult<PortfolioItemResponseDTO[], Error>;
type LiveObjectsQueryResult = QueryObserverResult<Set<string>, Error>;
type FavoritesQueryResult = QueryObserverResult<Map<string, string>, Error>;

const getStrategiesWithLiveObjectsAndFavorites = ({
  user,
  liveObjects,
  strategyResults,
  favorites,
}: {
  user: UserQueryResult;
  liveObjects: LiveObjectsQueryResult;
  strategyResults: StrategyQueryResult[];
  favorites: FavoritesQueryResult;
}): DSBStrategyItemResponseDTO[] => {
  const liveObjectTickers = liveObjects?.data ?? new Set<string>();

  return strategyResults
    .flatMap((result) => result.data ?? [])
    .map((datum) => {
      if (liveObjectTickers.has(datum.code)) {
        return {
          ...datum,
          isLive: true,
          virtualKey: datum.id,
          displayName: getStrategyDisplayNameBasedOnPreference({
            strategy: datum,
            preference: user.data?.displayNamePreference,
          }),
          favoriteId: favorites?.data?.get(datum.id),
        };
      }

      return {
        ...datum,
        virtualKey: datum.id,
        displayName: getStrategyDisplayNameBasedOnPreference({
          strategy: datum,
          preference: user.data?.displayNamePreference,
        }),
        favoriteId: favorites?.data?.get(datum.id),
      };
    });
};

const getIndexOptionsWithLiveObjectsAndFavorites = ({
  user,
  liveObjects,
  strategyResults,
  portfolioResults,
  favorites,
}: {
  user: UserQueryResult;
  liveObjects: LiveObjectsQueryResult;
  strategyResults: StrategyQueryResult[];
  portfolioResults: PortfolioQueryResult[];
  favorites: FavoritesQueryResult;
}): IndexOptionType[] => {
  const liveObjectTickers = liveObjects?.data ?? new Set<string>();

  return [...strategyResults, ...portfolioResults]
    .map((result) => result.data)
    .filter(notNull)
    .flat()
    .map((datum) => {
      const liveIdentifierToUse = 'code' in datum ? datum.code : undefined;
      const parameters = 'parameters' in datum ? datum.parameters : undefined;
      const isLivePortfolio = parameters?.hasCustomRebalancing || datum.type === PortfolioType.CONSTITUENT;
      const virtualKey = 'slug' in datum ? datum.portfolioId : datum.id;
      const displayName =
        'code' in datum
          ? getStrategyDisplayNameBasedOnPreference({ strategy: datum, preference: user.data?.displayNamePreference })
          : getPortfolioNameBasedOnPreference({ portfolio: datum, preference: user.data?.displayNamePreference });
      const favoriteId = favorites?.data?.get('portfolioId' in datum ? datum.portfolioId : datum.id);

      if ((liveIdentifierToUse && liveObjectTickers.has(liveIdentifierToUse)) || isLivePortfolio) {
        return { ...datum, isLive: true, virtualKey, displayName, favoriteId };
      }

      return { ...datum, virtualKey, displayName, favoriteId };
    });
};

/**
 * IMPORTANT: when adding new combine functions, please ensure that you use `markRawRecursive` on the data returned
 */
const combine = {
  universe: (
    results: [
      QueryObserverResult<IUserInfoResponse, Error>,
      QueryObserverResult<DataDiscoveryResponseDTO, Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<PortfolioItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
    ],
  ): {
    data: DataDiscoveryUniverse;
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, universe, evestmentFunds, morningstarFunds, privateTracks, portfolios, structuredProducts] = results;

    const data: DataDiscoveryUniverse = {
      strategy: (universe.data?.strategy ?? []).map((strategy) => ({
        ...strategy,
        displayName: getStrategyDisplayNameBasedOnPreference({
          strategy,
          preference: user.data?.displayNamePreference,
        }),
        virtualKey: strategy.code,
      })),
      colors: universe.data?.colors,
      portfolio: sortItemsByDisplayName(
        (portfolios.data ?? []).map((portfolio) => ({
          ...portfolio,
          displayName: getPortfolioNameBasedOnPreference({
            portfolio,
            preference: user.data?.displayNamePreference,
          }),
          virtualKey: portfolio.portfolioId,
        })),
      ),
    };

    for (const result of [evestmentFunds, morningstarFunds, privateTracks, structuredProducts]) {
      data.strategy = (data.strategy ?? []).concat(
        (result.data ?? []).map((strategy) => ({
          ...strategy,
          displayName: getStrategyDisplayNameBasedOnPreference({
            strategy,
            preference: user.data?.displayNamePreference,
          }),
          virtualKey: strategy.code,
        })),
      );
    }

    data.strategy = sortItemsByDisplayName((data.strategy as DSBStrategyItemResponseDTO[]) ?? []);

    return {
      data: markRawRecursive(data),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },

  universeWithEquities: (
    results: [
      QueryObserverResult<IUserInfoResponse, Error>,
      QueryObserverResult<DataDiscoveryResponseDTO, Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<PortfolioItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
    ],
  ): {
    data: DataDiscoveryUniverse;
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, universe, evestmentFunds, morningstarFunds, privateTracks, portfolios, stocks, structuredProducts] =
      results;

    const data: DataDiscoveryUniverse = {
      strategy: (universe.data?.strategy ?? []).map((strategy) => ({
        ...strategy,
        displayName: getStrategyDisplayNameBasedOnPreference({
          strategy,
          preference: user.data?.displayNamePreference,
        }),
        virtualKey: strategy.code,
      })),
      colors: universe.data?.colors,
      portfolio: sortItemsByDisplayName(
        (portfolios.data ?? []).map((portfolio) => ({
          ...portfolio,
          displayName: getPortfolioNameBasedOnPreference({
            portfolio,
            preference: user.data?.displayNamePreference,
          }),
          virtualKey: portfolio.portfolioId,
        })),
      ),
    };

    for (const result of [evestmentFunds, morningstarFunds, privateTracks, stocks, structuredProducts]) {
      data.strategy = (data.strategy ?? []).concat(
        (result.data ?? []).map((strategy) => ({
          ...strategy,
          displayName: getStrategyDisplayNameBasedOnPreference({
            strategy,
            preference: user.data?.displayNamePreference,
          }),
          virtualKey: strategy.code,
        })),
      );
    }

    data.strategy = sortItemsByDisplayName((data.strategy as DSBStrategyItemResponseDTO[]) ?? []);

    return {
      data: markRawRecursive(data),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  /**
   * Specifically does not combine the result with portfolios, because these are not shown on the Discover page for BofA
   */
  universeForBofA: (
    results: [QueryObserverResult<IUserInfoResponse, Error>, QueryObserverResult<DataDiscoveryResponseDTO, Error>],
  ): {
    data: DataDiscoveryUniverse;
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, universe] = results;

    const data: DataDiscoveryUniverse = {
      strategy: sortItemsByDisplayName(
        (universe.data?.strategy ?? []).map((strategy) => ({
          ...strategy,
          displayName: getStrategyDisplayNameBasedOnPreference({
            strategy,
            preference: user.data?.displayNamePreference,
          }),
          virtualKey: strategy.code,
        })),
      ),
      colors: universe.data?.colors,
    };

    return {
      data: markRawRecursive(data),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  universeMetrics: (
    results: QueryObserverResult<DataDiscoveryUniverseMetricsResponseDTO, Error>[],
  ): {
    data: DataDiscoveryUniverseMetricsResponseDTO;
    isLoading: boolean;
    isError: boolean;
  } => {
    const data: DataDiscoveryUniverseMetricsResponseDTO = {};

    for (const result of results) {
      Object.assign(data, result.data);
    }

    return {
      data: markRawRecursive(data),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  universeMetricsForPeriods: (
    results: QueryObserverResult<DataDiscoveryUniverseMetricsResponseDTO, Error>[],
    queries: Ref<DataDiscoveryMetricsRequestDTO[]>,
  ): Map<DiscoverPeriod, { data: DataDiscoveryUniverseMetricsResponseDTO; isLoading: boolean; isError: boolean }> => {
    const retval = new Map<
      DiscoverPeriod,
      { data: DataDiscoveryUniverseMetricsResponseDTO; isLoading: boolean; isError: boolean }
    >();

    // each query is duplicated, once for the universe, once for equities, once for evestment funds,
    // once for morningstar funds, once for private tracks, and once for structured products
    const queriesToUse = flatten(queries.value.map((query) => [query, query, query, query, query, query]));

    results.forEach((result, idx) => {
      const query = queriesToUse[idx];

      const dataForPeriod = retval.get(query.period);

      retval.set(query.period, {
        data: markRawRecursive(Object.assign(dataForPeriod?.data ?? {}, result.data)),
        isLoading: dataForPeriod?.isLoading || result.isLoading,
        isError: dataForPeriod?.isError || result.isError,
      });
    });

    return retval;
  },
  singleStrategyResultByCode: (
    results: [QueryObserverResult<IUserInfoResponse, Error>, QueryObserverResult<StrategyItemResponseDTO[], Error>],
  ): {
    data: Map<string, DSBStrategyItemResponseDTO>;
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, strategyResult] = results;
    const data = new Map<string, DSBStrategyItemResponseDTO>();

    for (const strategy of strategyResult.data ?? []) {
      data.set(
        strategy.code,
        Object.assign(strategy, {
          virtualKey: strategy.code,
          displayName: getStrategyDisplayNameBasedOnPreference({
            strategy,
            preference: user.data?.displayNamePreference,
          }),
        }),
      );
    }

    return {
      data: markRawRecursive(data),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  allStrategiesByCode: (
    results: [
      QueryObserverResult<IUserInfoResponse, Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
    ],
  ): {
    data: Map<string, DSBStrategyItemResponseDTO>;
    isLoading: boolean;
    isError: boolean;
  } => {
    const [
      user,
      plainStrategies,
      stocks,
      evestmentFunds,
      morningstarFunds,
      privateTracks,
      retiredStrategies,
      structuredProducts,
    ] = results;
    const data = new Map<string, DSBStrategyItemResponseDTO>();

    for (const strategy of [
      plainStrategies,
      stocks,
      evestmentFunds,
      morningstarFunds,
      privateTracks,
      retiredStrategies,
      structuredProducts,
    ]
      .map((result) => result.data)
      .filter(notNull)
      .flat() ?? []) {
      data.set(
        strategy.code,
        Object.assign(strategy, {
          virtualKey: strategy.code,
          displayName: getStrategyDisplayNameBasedOnPreference({
            strategy,
            preference: user.data?.displayNamePreference,
          }),
        }),
      );
    }

    return {
      data: markRawRecursive(data),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  allStrategiesById: (
    results: [
      QueryObserverResult<IUserInfoResponse, Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
    ],
  ): {
    data: Map<string, DSBStrategyItemResponseDTO>;
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, plainStrategies, stocks, evestmentFunds, morningstarFunds, privateTracks, structuredProducts] =
      results;
    const data = new Map<string, DSBStrategyItemResponseDTO>();

    for (const strategy of [
      plainStrategies,
      stocks,
      evestmentFunds,
      morningstarFunds,
      privateTracks,
      structuredProducts,
    ]
      .map((result) => result.data)
      .filter(notNull)
      .flat() ?? []) {
      data.set(
        strategy.id,
        Object.assign(strategy, {
          virtualKey: strategy.code,
          displayName: getStrategyDisplayNameBasedOnPreference({
            strategy,
            preference: user.data?.displayNamePreference,
          }),
        }),
      );
    }

    return {
      data: markRawRecursive(data),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  allStrategiesSortedByDisplayName: (
    results: [
      QueryObserverResult<IUserInfoResponse, Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
      QueryObserverResult<StrategyItemResponseDTO[], Error>,
    ],
  ): {
    data: DSBStrategyItemResponseDTO[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, plainStrategies, stocks, evestmentFunds, morningstarFunds, privateTracks, structuredProducts] =
      results;

    const data = [plainStrategies, stocks, evestmentFunds, morningstarFunds, privateTracks, structuredProducts]
      .map((result) => result.data)
      .filter(notNull)
      .flat()
      .map((strategy) => ({
        ...strategy,
        displayName: getStrategyDisplayNameBasedOnPreference({
          strategy,
          preference: user.data?.displayNamePreference,
        }),
        virtualKey: strategy.code,
      }));

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },

  /**
   * Only for use with DataTypes that may not be live:
   * - DataType.MARKET_DATA
   * - DataType.STOCK
   * - DataType.STRUCTURED_PRODUCT
   * - DataType.CASH
   * - DataType.SIGNAL
   */
  allStrategiesFromSingleResultAndSort: ([user, strategyQueryResult, favorites]: [
    QueryObserverResult<IUserInfoResponse, Error>,
    QueryObserverResult<StrategyItemResponseDTO[], Error>,
    QueryObserverResult<Map<string, string>, Error>,
  ]): {
    data: DSBStrategyItemResponseDTO[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const data: DSBStrategyItemResponseDTO[] = (strategyQueryResult.data ?? []).map((datum) => ({
      ...datum,
      virtualKey: datum.id,
      displayName: getStrategyDisplayNameBasedOnPreference({
        strategy: datum,
        preference: user.data?.displayNamePreference,
      }),
      favoriteId: favorites?.data?.get(datum.id),
    }));

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: [user, strategyQueryResult, favorites].some((result) => result.isLoading),
      isError: [user, strategyQueryResult, favorites].some((result) => result.isError),
    };
  },

  /**
   * Adds { isLive: true } if the strategy is live, which is applicable to the following DataTypes:
   * - DataType.STRATEGY
   * - DataType.PRIVATE_STRATEGY
   * - DataType.PRIVATE_TRACK
   * - DataType.PRIVATE_FUND
   * - DataType.THEMATIC
   * - DataType.PRIVATE_THEMATIC
   * - DataType.BENCHMARK (strategy or portfolio)
   * - DataType.PURE_FACTOR
   */
  allStrategiesFromSingleResultWithLiveObjectsAndSort: (
    results: [UserQueryResult, LiveObjectsQueryResult, StrategyQueryResult, FavoritesQueryResult],
  ): {
    data: DSBStrategyItemResponseDTO[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, liveObjects, strategyQueryResult, favorites] = results;
    const data = getStrategiesWithLiveObjectsAndFavorites({
      user,
      liveObjects,
      strategyResults: [strategyQueryResult],
      favorites,
    });

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  allStrategiesFromMultipleResultsWithLiveObjectsAndSort: (
    results: [UserQueryResult, LiveObjectsQueryResult, StrategyQueryResult, StrategyQueryResult, FavoritesQueryResult],
  ): {
    data: DSBStrategyItemResponseDTO[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, liveObjects, strategies, privateStrategies, favorites] = results;
    const data = getStrategiesWithLiveObjectsAndFavorites({
      user,
      liveObjects,
      strategyResults: [strategies, privateStrategies],
      favorites,
    });

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  allStrategiesFromMultipleResultsWithLiveObjectsRetiredAndSort: (
    results: [
      UserQueryResult,
      LiveObjectsQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      FavoritesQueryResult,
      StrategyQueryResult,
    ],
  ): {
    data: DSBStrategyItemResponseDTO[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, liveObjects, strategies, privateStrategies, favorites, retiredStrategies] = results;
    const data = getStrategiesWithLiveObjectsAndFavorites({
      user,
      liveObjects,
      strategyResults: [strategies, privateStrategies, retiredStrategies],
      favorites,
    });

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  allStrategyBenchmarksWithLiveObjectsAndSort: (
    results: [
      UserQueryResult,
      LiveObjectsQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      FavoritesQueryResult,
    ],
  ): {
    data: DSBStrategyItemResponseDTO[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, liveObjects, benchmarks, privateStrategies, morningstarFunds, favorites] = results;
    const data = getStrategiesWithLiveObjectsAndFavorites({
      user,
      liveObjects,
      strategyResults: [benchmarks, privateStrategies, morningstarFunds],
      favorites,
    });

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  allFundsWithLiveObjectsAndSort: (
    results: [
      UserQueryResult,
      LiveObjectsQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      FavoritesQueryResult,
    ],
  ): {
    data: DSBStrategyItemResponseDTO[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const [
      user,
      liveObjects,
      privateFunds,
      privateStrategies,
      evestmentFunds,
      morningstarFunds,
      equityFunds,
      favorites,
    ] = results;
    const data = getStrategiesWithLiveObjectsAndFavorites({
      user,
      liveObjects,
      strategyResults: [privateFunds, privateStrategies, evestmentFunds, morningstarFunds, equityFunds],
      favorites,
    });

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  /**
   * Selects strategy and portfolio benchmarks from the strategies and portfolios. Have made a separate combine function
   * than below due to addition of morningstarFunds / any separate logic for strategyAndPortfolioBenchmarks.
   */
  strategyAndPortfoliosWithLiveObjectsAndSort: (
    results: [
      UserQueryResult,
      LiveObjectsQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      PortfolioQueryResult,
      FavoritesQueryResult,
    ],
  ): {
    data: IndexOptionType[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, liveObjects, strategies, privateStrategies, portfolios, favorites] = results;
    const data = getIndexOptionsWithLiveObjectsAndFavorites({
      user,
      liveObjects,
      strategyResults: [strategies, privateStrategies],
      portfolioResults: [portfolios],
      favorites,
    });

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  strategyAndPortfoliosWithLiveObjectsRetiredAndSort: (
    results: [
      UserQueryResult,
      LiveObjectsQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      PortfolioQueryResult,
      FavoritesQueryResult,
      StrategyQueryResult,
    ],
  ): {
    data: IndexOptionType[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, liveObjects, strategies, privateStrategies, portfolios, favorites, retiredStrategies] = results;
    const data = getIndexOptionsWithLiveObjectsAndFavorites({
      user,
      liveObjects,
      strategyResults: [strategies, privateStrategies, retiredStrategies],
      portfolioResults: [portfolios],
      favorites,
    });

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  /**
   * Selects strategy and portfolio benchmarks from the strategies and portfolios matching DataType.BENCHMARK
   */
  strategyAndPortfolioBenchmarksWithLiveObjectsAndSort: (
    results: [
      UserQueryResult,
      LiveObjectsQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      StrategyQueryResult,
      PortfolioQueryResult,
      FavoritesQueryResult,
    ],
  ): {
    data: IndexOptionType[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const [user, liveObjects, strategyBenchmarks, privateStrategies, morningstarFunds, portfolioBenchmarks, favorites] =
      results;
    const data = getIndexOptionsWithLiveObjectsAndFavorites({
      user,
      liveObjects,
      strategyResults: [strategyBenchmarks, privateStrategies, morningstarFunds],
      portfolioResults: [portfolioBenchmarks],
      favorites,
    });

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    };
  },
  allPortfoliosWithLiveObjectsAndSort: ([user, liveObjects, portfolioQueryResult, favorites]: [
    QueryObserverResult<IUserInfoResponse, Error>,
    QueryObserverResult<Set<string>, Error>,
    QueryObserverResult<PortfolioItemResponseDTO[], Error>,
    QueryObserverResult<Map<string, string>, Error>,
  ]): {
    data: DSBPortfolioItemResponseDTO[];
    isLoading: boolean;
    isError: boolean;
  } => {
    const data: DSBPortfolioItemResponseDTO[] = (portfolioQueryResult.data ?? []).map(
      (datum): DSBPortfolioItemResponseDTO => {
        const parameters = 'parameters' in datum ? datum.parameters : null;
        const isLivePortfolio = parameters?.hasCustomRebalancing || datum.type === PortfolioType.CONSTITUENT;

        if (isLivePortfolio) {
          return {
            ...datum,
            isLive: true,
            virtualKey: datum.portfolioId,
            displayName: getPortfolioNameBasedOnPreference({
              portfolio: datum,
              preference: user.data?.displayNamePreference,
            }),
            favoriteId: favorites?.data?.get(datum.portfolioId),
          };
        }

        return {
          ...datum,
          virtualKey: datum.portfolioId,
          displayName: getPortfolioNameBasedOnPreference({
            portfolio: datum,
            preference: user.data?.displayNamePreference,
          }),
          favoriteId: favorites?.data?.get(datum.portfolioId),
        };
      },
    );

    return {
      data: markRawRecursive(sortItemsByDisplayName(data)),
      isLoading: [user, liveObjects, portfolioQueryResult, favorites].some((result) => result.isLoading),
      isError: [user, liveObjects, portfolioQueryResult, favorites].some((result) => result.isError),
    };
  },
};

/**
 * NOTE: on BofA, we also want to show private strategies
 */
export function usePlainStrategies(options: VQQueryOptions<DSBStrategyItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const { isBofAEnvironment } = getEnv();

  const selectFnToUse = isBofAEnvironment ? select.strategiesFromUniverseBofA : select.strategiesFromUniverse;

  const universeStrategies = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    select: selectFnToUse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracksTaggedAsStrategy = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    select: select.strategies,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  const retiredStrategies = queryOptions({
    queryKey: keys.retiredStrategies(),
    queryFn: ({ signal }) => getRetiredStrategies({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      liveObjects,
      universeStrategies,
      privateTracksTaggedAsStrategy,
      favorites,
      retiredStrategies,
    ]),
    combine: combine.allStrategiesFromMultipleResultsWithLiveObjectsRetiredAndSort,
  });
}

export function useStrategyAndMasterIndexes(options: VQQueryOptions<IndexOptionType[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const { isBofAEnvironment } = getEnv();

  const selectFnToUse = isBofAEnvironment ? select.strategiesFromUniverseBofA : select.strategiesFromUniverse;

  const universeStrategies = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    select: selectFnToUse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const qisBaskets = queryOptions({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    ...options,
    select: select.qisBaskets,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracksTaggedAsStrategy = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    select: select.strategies,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  const retiredStrategies = queryOptions({
    queryKey: keys.retiredStrategies(),
    queryFn: ({ signal }) => getRetiredStrategies({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      liveObjects,
      universeStrategies,
      privateTracksTaggedAsStrategy,
      qisBaskets,
      favorites,
      retiredStrategies,
    ]),
    combine: combine.strategyAndPortfoliosWithLiveObjectsRetiredAndSort,
  });
}

export function usePrivateTracks(options: VQQueryOptions<DSBStrategyItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracks = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    select: select.privateTracks,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, liveObjects, privateTracks, favorites]),
    combine: combine.allStrategiesFromSingleResultWithLiveObjectsAndSort,
  });
}

/**
 * By default, this composable will *include* stocks.
 * If you want to exclude stocks, you can do so by passing in `dataTypesToExclude`
 */
export function usePrivateFunds({
  dataTypesToExclude = [],
  options = {},
}: {
  /**
   * Currently only works with excluding stocks
   */
  dataTypesToExclude?: MaybeRef<DataType[]>;
  options?: VQQueryOptions<DSBStrategyItemResponseDTO[]>;
} = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateFunds = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    select: select.fundsFromUniverse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const evestmentFunds = queryOptions({
    queryKey: keys.evestmentFunds(),
    queryFn: ({ signal }) => getEvestmentFunds({ signal }),
    ...options,
    select: select.funds,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const morningstarFunds = queryOptions({
    queryKey: keys.morningstarFunds(),
    queryFn: ({ signal }) => getMorningstarFunds({ signal }),
    ...options,
    select: select.funds,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const equityFunds = queryOptions({
    queryKey: keys.equities(),

    // eslint-disable-next-line no-restricted-syntax
    queryFn: ({ signal }) => getEquities({ signal }),
    ...options,
    enabled: chainEnabled(
      computed(() => !unref(dataTypesToExclude).includes(DataType.STOCK)),
      options.enabled,
    ),
    select: select.funds,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracksTaggedAsFund = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    select: select.funds,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      liveObjects,
      privateFunds,
      privateTracksTaggedAsFund,
      evestmentFunds,
      morningstarFunds,
      equityFunds,
      favorites,
    ]),
    combine: combine.allFundsWithLiveObjectsAndSort,
  });
}

export function usePlainThematics(options: VQQueryOptions<DSBStrategyItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const { isBofAEnvironment } = getEnv();
  const selectFnToUse = isBofAEnvironment ? select.thematicsFromUniverseBofA : select.thematicsFromUniverse;
  const selectPrivateStrategiesFnToUse = isBofAEnvironment ? select.thematicsBofA : select.thematics;

  const thematics = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    select: selectFnToUse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracksTaggedAsThematic = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    select: selectPrivateStrategiesFnToUse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, liveObjects, thematics, privateTracksTaggedAsThematic, favorites]),
    combine: combine.allStrategiesFromMultipleResultsWithLiveObjectsAndSort,
  });
}

export function useThematicsAndEquityBaskets(options: VQQueryOptions<IndexOptionType[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const { isBofAEnvironment } = getEnv();
  const selectFnToUse = isBofAEnvironment ? select.thematicsFromUniverseBofA : select.thematicsFromUniverse;
  const selectPrivateStrategiesFnToUse = isBofAEnvironment ? select.thematicsBofA : select.thematics;

  const thematics = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    select: selectFnToUse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const equityBaskets = queryOptions({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    ...options,
    select: select.equityBaskets,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracksTaggedAsThematic = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    select: selectPrivateStrategiesFnToUse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      liveObjects,
      thematics,
      privateTracksTaggedAsThematic,
      equityBaskets,
      favorites,
    ]),
    combine: combine.strategyAndPortfoliosWithLiveObjectsAndSort,
  });
}

export function useJustStrategyBenchmarks(options: VQQueryOptions<DSBStrategyItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const benchmarks = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    select: select.strategyBenchmarksFromUniverse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const morningstarFunds = queryOptions({
    queryKey: keys.morningstarFunds(),
    queryFn: ({ signal }) => getMorningstarFunds({ signal }),
    ...options,
    select: select.benchmarks,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracksTaggedAsBenchmark = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    select: select.benchmarks,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      liveObjects,
      benchmarks,
      privateTracksTaggedAsBenchmark,
      morningstarFunds,
      favorites,
    ]),
    combine: combine.allStrategyBenchmarksWithLiveObjectsAndSort,
  });
}

export function useStrategyAndPortfolioBenchmarks(options: VQQueryOptions<IndexOptionType[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const strategyBenchmarks = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    select: select.strategyBenchmarksFromUniverse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const morningstarFunds = queryOptions({
    queryKey: keys.morningstarFunds(),
    queryFn: ({ signal }) => getMorningstarFunds({ signal }),
    ...options,
    select: select.benchmarks,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracksTaggedAsBenchmark = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    select: select.benchmarks,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const portfolioBenchmarks = queryOptions({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    ...options,
    select: select.portfolioBenchmarks,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      liveObjects,
      strategyBenchmarks,
      privateTracksTaggedAsBenchmark,
      morningstarFunds,
      portfolioBenchmarks,
      favorites,
    ]),
    combine: combine.strategyAndPortfolioBenchmarksWithLiveObjectsAndSort,
  });
}

export function usePureFactors(options: VQQueryOptions<IndexOptionType[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const pureFactors = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    select: select.pureFactorsFromUniverse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, liveObjects, pureFactors, favorites]),
    combine: combine.allStrategiesFromSingleResultWithLiveObjectsAndSort,
  });
}

export function useMarketData(options: VQQueryOptions<IndexOptionType[]> = {}) {
  const user = useUserQueryOptions(options);

  const marketData = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    select: select.marketDataFromUniverse,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, marketData, favorites]),
    combine: combine.allStrategiesFromSingleResultAndSort,
  });
}

export function usePortfolios(options: VQQueryOptions<DSBPortfolioItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const portfolios = queryOptions({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    ...options,
    select: select.allPortfolios,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, liveObjects, portfolios, favorites]),
    combine: combine.allPortfoliosWithLiveObjectsAndSort,
  });
}

export function useNonBasketPortfolios(options: VQQueryOptions<DSBPortfolioItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const liveObjects = queryOptions({
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }) => getLiveObjects(signal),
    ...options,
    select: liveObjectsSelect.justCodes,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const portfolios = queryOptions({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    ...options,
    select: select.nonBasketPortfolios,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, liveObjects, portfolios, favorites]),
    combine: combine.allPortfoliosWithLiveObjectsAndSort,
  });
}

/**
 * Get the whole universe
 * This combines data from three different APIs to form the universe
 *
 * As of mid September 2025, we removed stocks from this composable.
 *
 * Given that we do NOT want to show stocks on Data Universe, and this composable is only used
 * for Data Universe, this is fine.
 */
export function useUniverse(options: VQQueryOptions = {}) {
  if (import.meta.env.VITE_CLIENT === 'BofA') return useUniverseForBofA(options);

  const user = useUserQueryOptions(options);

  const universe = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const portfolios = queryOptions({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const evestmentFunds = queryOptions({
    queryKey: keys.evestmentFunds(),
    queryFn: ({ signal }) => getEvestmentFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const morningstarFunds = queryOptions({
    queryKey: keys.morningstarFunds(),
    queryFn: ({ signal }) => getMorningstarFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracks = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const structuredProducts = queryOptions({
    queryKey: keys.structuredProducts(),
    queryFn: ({ signal }) => getStructuredProducts({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      universe,
      evestmentFunds,
      morningstarFunds,
      privateTracks,
      portfolios,
      structuredProducts,
    ]),
    combine: combine.universe,
    ...options,
  });
}

/**
 * This is too much data for our platform, currently only used by:
 * - Generic Faux Whitelabel team (647)
 * - Equity Cash Test team (469)
 */
export function useUniverseWithEquities(options: VQQueryOptions = {}) {
  if (import.meta.env.VITE_CLIENT === 'BofA') return useUniverseForBofA(options);

  const user = useUserQueryOptions(options);

  const universe = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const portfolios = queryOptions({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const evestmentFunds = queryOptions({
    queryKey: keys.evestmentFunds(),
    queryFn: ({ signal }) => getEvestmentFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const morningstarFunds = queryOptions({
    queryKey: keys.morningstarFunds(),
    queryFn: ({ signal }) => getMorningstarFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracks = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const equities = queryOptions({
    queryKey: keys.equities(),

    // eslint-disable-next-line no-restricted-syntax
    queryFn: ({ signal }) => getEquities({ signal }),
    ...options,
    select: select.addCompositeTicker,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const structuredProducts = queryOptions({
    queryKey: keys.structuredProducts(),
    queryFn: ({ signal }) => getStructuredProducts({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      universe,
      evestmentFunds,
      morningstarFunds,
      privateTracks,
      portfolios,
      equities,
      structuredProducts,
    ]),
    combine: combine.universeWithEquities,
    ...options,
  });
}

/**
 * On the BofA environment, we only want to show QIS strategies and benchmarks
 * this includes the types: Strategy, Benchmark, Private Strategy
 *
 * This should only be the case on the data universe page, on the DSB we want to show all
 * the time series entities available to them
 */
export function useUniverseForBofA(options: VQQueryOptions = {}) {
  const user = useUserQueryOptions(options);

  const universe = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    select: select.universeForBofA,
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, universe]),
    combine: combine.universeForBofA,
    ...options,
  });
}

/**
 * Get all strategies.
 *
 * By default, this composable will *include* stocks.
 * If you want to exclude stocks, you can do so by passing in `dataTypesToExclude`
 */
export function useAllStrategies({
  dataTypesToExclude = [],
  options = {},
}: {
  /**
   * Currently only works with excluding stocks
   */
  dataTypesToExclude?: MaybeRef<DataType[]>;
  options?: VQQueryOptions<DSBStrategyItemResponseDTO[]>;
} = {}) {
  const user = useUserQueryOptions(options);

  const plainStrategies = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    select: select.allStrategies,
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const equities = queryOptions({
    queryKey: keys.equities(),

    // eslint-disable-next-line no-restricted-syntax
    queryFn: ({ signal }) => getEquities({ signal }),
    ...options,
    enabled: chainEnabled(
      computed(() => !unref(dataTypesToExclude).includes(DataType.STOCK)),
      options.enabled,
    ),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const evestmentFunds = queryOptions({
    queryKey: keys.evestmentFunds(),
    queryFn: ({ signal }) => getEvestmentFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const morningstarFunds = queryOptions({
    queryKey: keys.morningstarFunds(),
    queryFn: ({ signal }) => getMorningstarFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracks = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const structuredProducts = queryOptions({
    queryKey: keys.structuredProducts(),
    queryFn: ({ signal }) => getStructuredProducts({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      plainStrategies,
      equities,
      evestmentFunds,
      morningstarFunds,
      privateTracks,
      structuredProducts,
    ]),
    combine: combine.allStrategiesSortedByDisplayName,
    ...options,
  });
}

/**
 * Get a map of strategy id -> strategy
 *
 * By default, this composable will *include* stocks.
 * If you want to exclude stocks, you can do so by passing in `dataTypesToExclude`
 */
export function useAllStrategiesById({
  dataTypesToExclude = [],
  options = {},
}: {
  /**
   * Currently only works with excluding stocks
   */
  dataTypesToExclude?: MaybeRef<DataType[]>;
  options?: VQQueryOptions<Map<string, DSBStrategyItemResponseDTO>>;
} = {}) {
  const user = useUserQueryOptions(options);

  const plainStrategies = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    select: select.allStrategies,
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const equities = queryOptions({
    queryKey: keys.equities(),

    // eslint-disable-next-line no-restricted-syntax
    queryFn: ({ signal }) => getEquities({ signal }),
    ...options,
    enabled: chainEnabled(
      computed(() => !unref(dataTypesToExclude).includes(DataType.STOCK)),
      options.enabled,
    ),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const evestmentFunds = queryOptions({
    queryKey: keys.evestmentFunds(),
    queryFn: ({ signal }) => getEvestmentFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const morningstarFunds = queryOptions({
    queryKey: keys.morningstarFunds(),
    queryFn: ({ signal }) => getMorningstarFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracks = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const structuredProducts = queryOptions({
    queryKey: keys.structuredProducts(),
    queryFn: ({ signal }) => getStructuredProducts({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      plainStrategies,
      equities,
      evestmentFunds,
      morningstarFunds,
      privateTracks,
      structuredProducts,
    ]),
    combine: combine.allStrategiesById,
  });
}

/**
 * Get a map of strategy code -> strategy
 *
 * By default, this composable will *include* stocks.
 * If you want to exclude stocks, you can do so by passing in `dataTypesToExclude`
 */
export function useAllStrategiesByCode(
  {
    dataTypesToExclude = [],
    options = {},
  }: {
    /**
     * Currently only works with excluding stocks
     */
    dataTypesToExclude?: MaybeRef<DataType[]>;
    options?: VQQueryOptions<Map<string, DSBStrategyItemResponseDTO>>;
  } = {
    dataTypesToExclude: [],
    options: {},
  },
) {
  const user = useUserQueryOptions(options);

  const plainStrategies = queryOptions({
    queryKey: keys.limitedUniverse(),
    queryFn: ({ signal }) => getUniverse({ signal }),
    select: select.allStrategies,
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const equities = queryOptions({
    queryKey: keys.equities(),

    // eslint-disable-next-line no-restricted-syntax
    queryFn: ({ signal }) => getEquities({ signal }),
    ...options,
    enabled: chainEnabled(
      computed(() => !unref(dataTypesToExclude).includes(DataType.STOCK)),
      options.enabled,
    ),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const evestmentFunds = queryOptions({
    queryKey: keys.evestmentFunds(),
    queryFn: ({ signal }) => getEvestmentFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const morningstarFunds = queryOptions({
    queryKey: keys.morningstarFunds(),
    queryFn: ({ signal }) => getMorningstarFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracks = queryOptions({
    queryKey: keys.privateTracks(),
    queryFn: ({ signal }) => getPrivateTracks({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const retiredStrategies = queryOptions({
    queryKey: keys.retiredStrategies(),
    queryFn: ({ signal }) => getRetiredStrategies({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const structuredProducts = queryOptions({
    queryKey: keys.structuredProducts(),
    queryFn: ({ signal }) => getStructuredProducts({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      user,
      plainStrategies,
      equities,
      evestmentFunds,
      morningstarFunds,
      privateTracks,
      retiredStrategies,
      structuredProducts,
    ]),
    combine: combine.allStrategiesByCode,
  });
}

export function useEquities(options: VQQueryOptions<DSBStrategyItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const equities = queryOptions({
    queryKey: keys.equities(),

    // eslint-disable-next-line no-restricted-syntax
    queryFn: ({ signal }) => getEquities({ signal }),
    ...options,
    select: select.addCompositeTicker,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, equities, favorites]),
    combine: combine.allStrategiesFromSingleResultAndSort,
  });
}

export function useStructuredProducts(options: VQQueryOptions<DSBStrategyItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const structuredProducts = queryOptions({
    queryKey: keys.structuredProducts(),
    queryFn: ({ signal }) => getStructuredProducts({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, structuredProducts, favorites]),
    combine: combine.allStrategiesFromSingleResultAndSort,
  });
}

export function useCash(options: VQQueryOptions<DSBStrategyItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const cashStrategies = queryOptions({
    queryKey: keys.cash(),
    queryFn: ({ signal }) => getCash({ signal }),
    ...options,
    // Todo: we really shouldn't be stripping the suffix here. The place which needs a short ticker should be the place
    // stripping out the suffix, not us here stripping it out pre-maturely in the global data layer, as we still want to
    // show it in various places such as the factsheet. (note: factsheet does not use the global data layer though)
    select: select.stripCurncySuffixFromTicker,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, cashStrategies, favorites]),
    combine: combine.allStrategiesFromSingleResultAndSort,
  });
}

export function useSignals(options: VQQueryOptions<DSBStrategyItemResponseDTO[]> = {}) {
  const user = useUserQueryOptions(options);

  const signalStrategies = queryOptions({
    queryKey: keys.signals(),
    queryFn: ({ signal }) => getSignals({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const favorites = getFavoritesQueryOptions(options);

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, signalStrategies, favorites]),
    combine: combine.allStrategiesFromSingleResultAndSort,
  });
}

/**
 *
 * Get all eVestment Funds
 */
export function useEvestmentFunds(options: VQQueryOptions<DSBStrategyItemResponseDTO[]> = {}) {
  return useQuery({
    queryKey: keys.evestmentFunds(),
    queryFn: ({ signal }) => getEvestmentFunds({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get all portfolios.
 * For a map of portfolios, use {@link useAllPortfoliosBySlug} instead
 */
export function useAllPortfolios(options: VQQueryOptions<DSBPortfolioItemResponseDTO[]> = {}) {
  return useQuery({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    select: select.allPortfolios,
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get an array of portfolio names.
 */
export function useAllPortfolioNames(options: VQQueryOptions<string[]> = {}) {
  return useQuery({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    select: select.allPortfolioNames,
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get a map of portfolio id -> portfolio
 */
export function useAllPortfoliosById(options: VQQueryOptions<Map<string, DSBPortfolioItemResponseDTO>> = {}) {
  return useQuery({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    select: select.allPortfoliosById,
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get a map of portfolio slug -> portfolio
 */
export function useAllPortfoliosBySlug(options: VQQueryOptions<Map<string, DSBPortfolioItemResponseDTO>> = {}) {
  return useQuery({
    queryKey: keys.portfolios(),
    queryFn: ({ signal }) => getPortfolios({ signal }),
    select: select.allPortfoliosBySlug,
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Gets a summary of portfolios which strategies can be added to.
 */
export function usePortfolioSummary(options: VQQueryOptions<PortfolioSummaryResponseDTO[]> = {}) {
  return useQuery({
    queryKey: keys.portfolioSummary(),
    queryFn: ({ signal }) => getPortfolioSummary({ signal }).then(markRawRecursive),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get metrics data for all indices (does NOT work with volScaling).
 * Calculation method: generated tracks and metrics from the cache data from Redis.
 *
 * Stocks are conditionally included via the extraDataTypesToInclude parameter.
 */
export function useNonVolScaledDiscoverUniverseMetrics({
  params,
  options = {},
  /**
   * Currently only works with including stocks.
   */
  extraDataTypesToInclude = [],
}: {
  params: Ref<DataDiscoveryMetricsRequestDTO>;
  options?: VQQueryOptions<DataDiscoveryUniverseMetricsResponseDTO, Error>;
  extraDataTypesToInclude?: MaybeRef<DataType[]>;
}) {
  const plainStrategiesMetrics = queryOptions({
    queryKey: keys.nonVolScaledUniverseMetrics(params),
    queryFn: ({ signal }) => getNonVolScaledUniverseMetrics({ params: params.value, signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const evestmentFundsMetrics = queryOptions({
    queryKey: keys.evestmentFundsMetrics(params),
    queryFn: ({ signal }) => getEvestmentFundsMetrics({ params: params.value, signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const equitiesMetrics = queryOptions({
    queryKey: keys.equitiesMetrics(params),
    queryFn: ({ signal }) => getEquitiesMetrics({ params: params.value, signal }),
    ...options,
    enabled: chainEnabled(
      computed(() => unref(extraDataTypesToInclude).includes(DataType.STOCK)),
      options.enabled,
    ),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const morningstarFundsMetrics = queryOptions({
    queryKey: keys.morningstarFundsMetrics(params),
    queryFn: ({ signal }) => getMorningstarFundsMetrics({ params: params.value, signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const privateTracksMetrics = queryOptions({
    queryKey: keys.privateTracksMetrics(params),
    queryFn: ({ signal }) => getPrivateTracksMetrics({ params: params.value, signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const structuredProductsMetrics = queryOptions({
    queryKey: keys.structuredProductsMetrics(params),
    queryFn: ({ signal }) => getStructuredProductMetrics({ params: params.value, signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([
      plainStrategiesMetrics,
      equitiesMetrics,
      evestmentFundsMetrics,
      morningstarFundsMetrics,
      privateTracksMetrics,
      structuredProductsMetrics,
    ]),
    combine: combine.universeMetrics,
    ...options,
  });
}

/**
 * Get metrics data for all portfolios (does NOT work with volScaling).
 * Calculation method: generated tracks and metrics from the cache data from Redis.
 */
export function useNonVolScaledDiscoverPortfolioMetrics(
  params: Ref<DataDiscoveryMetricsRequestDTO>,
  options: VQQueryOptions<DataDiscoveryPortfolioMetricsResponseDTO, Error> = {},
) {
  return useQuery({
    queryKey: keys.nonVolScaledPortfoliosMetrics(params),
    queryFn: ({ signal }) => getNonVolScaledPortfoliosMetrics({ params: params.value, signal }).then(markRawRecursive),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get metrics data for all indices (does NOT work with volScaling).
 * Calculation method: generated tracks and metrics from the cache data from Redis.
 *
 * Stocks are conditionally included via the extraDataTypesToInclude parameter.
 */
export function useNonVolScaledDiscoverUniverseMetricsForPeriods({
  queries,
  options = {},
  /**
   * Currently only works with including stocks.
   */
  extraDataTypesToInclude = [],
}: {
  queries: Ref<DataDiscoveryMetricsRequestDTO[]>;
  options?: VQQueryOptions<DataDiscoveryUniverseMetricsResponseDTO>;
  extraDataTypesToInclude?: MaybeRef<DataType[]>;
}) {
  const shouldFetchStocksEnabled = computed(() => unref(extraDataTypesToInclude).includes(DataType.STOCK));
  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: computed(() => {
      const allQueries = [];

      for (const params of queries.value) {
        // Add universe metrics query
        allQueries.push(
          queryOptions({
            queryKey: keys.nonVolScaledUniverseMetrics(ref(params)),
            queryFn: ({ signal }) => getNonVolScaledUniverseMetrics({ params, signal }),
            ...options,
            staleTime: Number.POSITIVE_INFINITY,
            refetchOnWindowFocus: false,
          }),
          queryOptions({
            queryKey: keys.equitiesMetrics(ref(params)),
            queryFn: ({ signal }) => getEquitiesMetrics({ params, signal }),
            ...options,
            enabled: chainEnabled(shouldFetchStocksEnabled, options.enabled),
            staleTime: Number.POSITIVE_INFINITY,
            refetchOnWindowFocus: false,
          }),
          queryOptions({
            queryKey: keys.evestmentFundsMetrics(ref(params)),
            queryFn: ({ signal }) => getEvestmentFundsMetrics({ params, signal }),
            ...options,
            staleTime: Number.POSITIVE_INFINITY,
            refetchOnWindowFocus: false,
          }),
          queryOptions({
            queryKey: keys.morningstarFundsMetrics(ref(params)),
            queryFn: ({ signal }) => getMorningstarFundsMetrics({ params, signal }),
            ...options,
            staleTime: Number.POSITIVE_INFINITY,
            refetchOnWindowFocus: false,
          }),
          queryOptions({
            queryKey: keys.privateTracksMetrics(ref(params)),
            queryFn: ({ signal }) => getPrivateTracksMetrics({ params, signal }),
            ...options,
            staleTime: Number.POSITIVE_INFINITY,
            refetchOnWindowFocus: false,
          }),
          queryOptions({
            queryKey: keys.structuredProductsMetrics(ref(params)),
            queryFn: ({ signal }) => getStructuredProductMetrics({ params, signal }),
            ...options,
            staleTime: Number.POSITIVE_INFINITY,
            refetchOnWindowFocus: false,
          }),
        );
      }

      return allQueries;
    }),
    combine: (results) => combine.universeMetricsForPeriods(results, queries),
    ...options,
  });
}

/**
 * Get metrics data for all portfolios (does NOT work with volScaling).
 * Calculation method: generated tracks and metrics from the cache data from Redis.
 */
export function useNonVolScaledDiscoverPortfolioMetricsForPeriods(
  queries: Ref<DataDiscoveryMetricsRequestDTO[]>,
  options: VQQueryOptions<DataDiscoveryPortfolioMetricsResponseDTO> = {},
) {
  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: computed((): UseQueryOptions<DataDiscoveryPortfolioMetricsResponseDTO>[] =>
      queries.value.map((params) => ({
        queryKey: keys.nonVolScaledPortfoliosMetrics(ref(params)),
        queryFn: ({ signal }) => getNonVolScaledPortfoliosMetrics({ params, signal }).then(markRawRecursive),
        ...options,
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
      })),
    ),
  });
}

export function useQuantSpacePositions(
  params: Ref<DataDiscoveryUniverseQuantSpaceRequestDTO>,
  options: VQQueryOptions<DataDiscoveryUniverseQuantSpaceResponseDTO, Error> = {},
) {
  return useQuery({
    queryKey: keys.quantSpacePositions(params),
    queryFn: ({ signal }) => getQuantSpacePositions({ params: params.value, signal }).then(markRawRecursive),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

export function useStrategyCategories(options: VQQueryOptions<StrategyCategoryResponseDTO[]> = {}) {
  return useQuery({
    queryKey: keys.strategyCategories(),
    queryFn: ({ signal }) => getStrategyCategories({ signal }).then(markRawRecursive),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

export function useRetiredStrategies(options: VQQueryOptions<StrategyItemResponseDTO[]> = {}) {
  return useQuery({
    queryKey: keys.retiredStrategies(),
    queryFn: ({ signal }) => getRetiredStrategies({ signal }).then(markRawRecursive),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

/**
 * Get metrics data for strategies for a given volScaling setting.
 * Calculation method: apply volScaling and ensure its calculated by C++.
 */
export function useVolScaledUniverseMetrics(
  queries: Ref<DataDiscoveryVolScaledStrategyMetricsRequestDTO[]>,
  options: VQQueryOptions<DataDiscoveryMetricsVolScaledResponseDTO> = {},
) {
  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: computed((): UseQueryOptions<{ result: IMetric }, Error>[] => {
      return queries.value.map((params) => ({
        queryKey: keys.volScaledUniverseMetrics(ref(params)),
        queryFn: ({ signal }) => getVolScaledUniverseMetrics({ params, signal }).then(markRawRecursive),
        ...options,
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
      }));
    }),
  });
}

/**
 * Get metrics data for portfolios for a given volScaling setting.
 * Calculation method: apply volScaling and ensure its calculated by C++.
 */
export function useVolScaledPortfolioMetrics(
  queries: Ref<DataDiscoveryVolScaledPortfolioMetricsRequestDTO[]>,
  options: VQQueryOptions<DataDiscoveryMetricsVolScaledResponseDTO> = {},
) {
  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: computed((): UseQueryOptions<{ result: IMetric }, Error>[] => {
      return queries.value.map((params) => ({
        queryKey: keys.volScaledPortfolioMetrics(ref(params)),
        queryFn: ({ signal }) => getVolScaledPortfolioMetrics({ params, signal }).then(markRawRecursive),
        ...options,
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
      }));
    }),
  });
}

/**
 * Fetch foreign exchange (FX) rates from other currencies to USD fx rates for a specific date.
 *
 * @param fxDate - The date for which to fetch the FX rates. Pass this parameter only if the user is not selecting the
 * date using a date picker component on the UI.
 * @param options - The options for the query
 * @returns FX rates map from other currencies to USD rates on the given date, example, JPY: 0.5 meaning JPY to USD exchange rate is 0.5 at the given date
 */
export function useFxRates(fxDate?: Ref<string | null>, options: VQQueryOptions<{ [key: string]: number }> = {}) {
  const constituentAnalysisDate = useConstituentAnalysisDate();

  const route = useRoute();
  const { isOnConstituentRisk } = useRouteChecks(route);
  const isOnPositionView = useIsOnPositionView(route);

  const dateToUse = computed(() => {
    if (fxDate?.value) return fxDate.value;

    if (isOnConstituentRisk.value || isOnPositionView.value) {
      return constituentAnalysisDate.value;
    }

    return null;
  });

  const query = computed(() => {
    return {
      date: dateToUse.value ?? undefined,
    };
  });

  const enabled = chainEnabled(
    options.enabled,
    computed(() => !!dateToUse.value),
  );

  return useLoadingWhenEnabledQuery(
    useQuery({
      queryKey: keys.fxRates(dateToUse),
      queryFn: ({ signal }) => getFxRates(unwrap(query), signal).then(markRawRecursive),
      ...options,
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
    }),
    enabled,
  );
}

/**
 * Returns all the signals available on the platform
 * Currently only used for HSBC
 * Warning: There is no access right implemented for this endpoint yet, use carefully
 * @param options
 * @returns StrategyListItemResponseDTO[]
 */
/**
 * Get a map of strategy code -> strategy
 */
export function useAllSignalsByCode(options: VQQueryOptions<Map<string, StrategyItemResponseDTO>> = {}) {
  const user = useUserQueryOptions(options);

  const signals = queryOptions({
    queryKey: keys.signals(),
    queryFn: ({ signal }) => getSignals({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, signals]),
    combine: combine.singleStrategyResultByCode,
  });
}

/**
 * Returns cash items in a map of code -> strategy
 */
export function useAllCashStrategiesByCode(options: VQQueryOptions<Map<string, DSBStrategyItemResponseDTO>> = {}) {
  const user = useUserQueryOptions(options);

  const cash = queryOptions({
    queryKey: keys.cash(),
    queryFn: ({ signal }) => getCash({ signal }),
    ...options,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line no-restricted-syntax
  return useQueries({
    queries: withLimitedQueryFns([user, cash]),
    combine: combine.singleStrategyResultByCode,
  });
}
